<template>
  <div class="make-model-modal">
    <div class="make-model-modal__dismiss" @click="closeClicked"></div>
    <div class="make-model-modal__container">
      <div class="make-model-modal__splitter" v-show="isMainModalVisible">
        <div class="make-model-modal__nav-container">
          <button @click="closeClicked" class="make-model-modal__close-icon border-none" aria-label="Close vehicle search modal" />
        </div>

        <div class="make-model-modal__search-container" :class="{ borderless: pillItemsSelected }">
          <div class="make-model-modal__search-icon"></div>
          <form-kit
            type="text"
            name="search"
            aria-label="Search"
            :placeholder="makeModelModalProps.searchPlaceholder"
            outer-class="make-model-modal__search-text-outer"
            wrapper-class="make-model-modal__search-text-wrapper"
            input-class="make-model-modal__search-text"
            @focus="showSuggestions"
          />
        </div>

        <div class="make-model-modal__pill-items-wrapper" v-show="pillItemsSelected">
          <div class="make-model-modal__pill-items-container">
            <PillItem class="make-model-pill-item" v-for="make in selectStore.selectedMakes" :key="make" :data-model="make" @remove="removeMake" />

            <br />

            <PillItem
              class="make-model-pill-item"
              v-for="(modelObject, index) in selectStore.selectedModels"
              :key="index"
              display-text-field="modelName"
              :data-model="modelObject"
              @remove="removeModel"
            />
          </div>
        </div>

        <div class="make-model-modal__options-container">
          {{ makeModelModalProps.searchOptionHeading }}
          <button class="make-model-modal__clear button-as-link" @click="clearAll">{{ makeModelModalProps.clearOptionText }}</button>
        </div>

        <div class="make-model-modal__body-content">
          <div class="make-model-modal__sections">
            <ModalSection
              v-for="(modalSection, index) in getEnrichedMainModalSections()"
              :key="index"
              :title="modalSection.title"
              :items="modalSection.items"
              :preview-item-display-field="modalSection.previewItemDisplayField"
              :disabled="modalSection.disabled"
              :value-symbol="modalSection.valueSymbol"
              :context-object="modalSection"
              @remove-preview-item="modalSection.removePreviewItemCallback"
              @section-clicked="showModal(modalSection)"
            />
          </div>

          <div
            class="make-model-modal__category-container"
            v-for="(categoryItem, index) in modalCategories"
            :key="index"
            @click="clickCategoryModals(categoryItem.name)"
          >
            <div class="make-model-modal__category-heading-container">
              <div :class="['make-model-modal__category-heading-icon', categoryItem.name]"></div>
              <div class="make-model-modal__category-heading">{{ categoryItem.title }}</div>
              <div :class="{ 'make-model-modal__category-collapse-icon': true, collapsed: categoryItem.collapsed }"></div>
            </div>
            <div :class="{ 'make-model-modal__category-sections': true, collapsed: categoryItem.collapsed }">
              <ModalSection
                v-for="(modalSection, catIndex) in getEnrichedCategoryModalSections(categoryItem.name)"
                :key="catIndex"
                :title="modalSection.title"
                :items="modalSection.items"
                :preview-item-display-field="modalSection.previewItemDisplayField"
                :disabled="modalSection.disabled"
                :context-object="modalSection"
                @remove-preview-item="modalSection.removePreviewItemCallback"
                @section-clicked="showModal(modalSection)"
              />
            </div>
          </div>
        </div>

        <div class="make-model-modal__search-button-container">
          <form-kit
            type="button"
            ref="findmycar"
            name="findmycar"
            outer-class="make-model-modal__search-button-wrapper"
            input-class="btn btn-primary make-model-modal__search-button"
            :disabled="availableStore.totalCount === 0"
            @click="performSearch"
          >
            {{ makeModelModalProps.searchButtonText }} {{ resultCount }}
          </form-kit>
        </div>
      </div>

      <component v-if="!isMainModalVisible" :is="modalToDisplay" @close="closeModal" @close-all="closeAll"></component>
    </div>
  </div>
</template>

<script>
import { useCarSelectStore } from '../../foundation/stores/carSelectStore';
import { useCarAvailableStore } from '../../foundation/stores/carAvailableStore';
import { useContentStore } from '../../foundation/stores/contentStore';
import { defineAsyncComponent } from 'vue';

import ModalSection from './ModalSection.vue';
import PillItem from '../form-fields/PillItem.vue';
import { useUrlBuilder } from '../../composables/useUrlBuilder';
import { useUrlNavigator } from '../../composables/useUrlNavigator';
import { useRouter } from 'vue-router';
import { mapState } from 'pinia';
import { AnalyticsEvents, AnalyticsEventTypes } from '../../foundation/enums/AnalyticsEvents';
import { RoutesEnum } from '../../foundation/routeMappings';

export default {
  name: 'MakeModelModal',
  components: {
    ModalSection,
    PillItem,
    SuggestionModal: defineAsyncComponent(() => import('./SuggestionModal.vue')),
    MakeModal: defineAsyncComponent(() => import('./MakeModal.vue')),
    ModelModal: defineAsyncComponent(() => import('./ModelModal.vue')),
    BadgeModal: defineAsyncComponent(() => import('./BadgeModal.vue')),
    YearModal: defineAsyncComponent(() => import('./YearModal.vue')),
    PriceModal: defineAsyncComponent(() => import('./PriceModal.vue')),
    BodyTypeModal: defineAsyncComponent(() => import('./BodyTypeModal.vue')),
    FuelTypeModal: defineAsyncComponent(() => import('./FuelTypeModal.vue')),
    LifestyleModal: defineAsyncComponent(() => import('./LifestyleModal.vue')),
    TowingCapacityModal: defineAsyncComponent(() => import('./TowingCapacityModal.vue')),
    EmissionsRatingModal: defineAsyncComponent(() => import('./EmissionsRatingModal.vue')),
    DrivingRangeModal: defineAsyncComponent(() => import('./DrivingRangeModal.vue')),
    TransmissionTypeModal: defineAsyncComponent(() => import('./TransmissionTypeModal.vue')),
    DriveTypeModal: defineAsyncComponent(() => import('./DriveTypeModal.vue')),
    EngineTypeModal: defineAsyncComponent(() => import('./EngineTypeModal.vue')),
    CylinderModal: defineAsyncComponent(() => import('./CylinderModal.vue')),
    EngineCapacityModal: defineAsyncComponent(() => import('./EngineCapacityModal.vue')),
    FuelScoreModal: defineAsyncComponent(() => import('./FuelScoreModal.vue')),
    SafetyRatingModal: defineAsyncComponent(() => import('./SafetyRatingModal.vue')),
    SeatModal: defineAsyncComponent(() => import('./SeatModal.vue')),
    DoorModal: defineAsyncComponent(() => import('./DoorModal.vue')),
    FeatureModal: defineAsyncComponent(() => import('./FeatureModal.vue')),
    SortModal: defineAsyncComponent(() => import('./SortModal.vue')),
  },
  emits: ['close-modal'],
  setup() {
    const selectStore = useCarSelectStore();
    const availableStore = useCarAvailableStore();
    const urlBuilder = useUrlBuilder();
    const router = useRouter();
    const urlNavigator = useUrlNavigator(router);
    const contentStore = useContentStore();

    contentStore.fetchMakeModelModal();

    // perform vehicle search result count on state change
    selectStore.$subscribe(availableStore.doSearch);

    return { selectStore, urlBuilder, urlNavigator, availableStore, router };
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    modalToDisplayOnLoad: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      engineCategoryCollapsed: false,
      modalCategories: [
        {
          name: 'engine-transmission',
          title: 'Engine & transmission',
          collapsed: false,
        },
        {
          name: 'electric',
          title: 'Electric',
          collapsed: false,
        },
        {
          name: 'safety',
          title: 'Safety',
          collapsed: false,
        },
        {
          name: 'style',
          title: 'Style',
          collapsed: false,
        },
      ],
      modalSections: [
        {
          name: 'sort-modal',
          title: 'Sort',
          modalOnly: true,
          isModalVisible: false,
          statePropertyName: 'selectedSortOrder',
        },
        {
          name: 'make-modal',
          title: 'Make',
          removePreviewItemCallback: this.removeMake,
          isModalVisible: false,
          statePropertyName: 'selectedMakes',
        },
        {
          name: 'model-modal',
          title: 'Model',
          previewItemDisplayField: 'modelName',
          removePreviewItemCallback: this.removeModel,
          isModalVisible: false,
          statePropertyName: 'selectedModels',
        },
        {
          name: 'badge-modal',
          title: 'Badge',
          previewItemDisplayField: 'badgeName',
          isModalVisible: false,
          statePropertyName: 'selectedBadges',
        },
        {
          name: 'year-modal',
          title: 'Year',
          removePreviewItemCallback: this.removeRange,
          isModalVisible: false,
          statePropertyName: 'selectedYear',
        },
        {
          name: 'price-modal',
          title: 'Price',
          removePreviewItemCallback: this.removeRange,
          isModalVisible: false,
          statePropertyName: 'selectedPrice',
          valueSymbol: '$',
        },
        {
          name: 'body-type-modal',
          title: 'Body type',
          isModalVisible: false,
          statePropertyName: 'selectedBodyTypes',
        },
        {
          name: 'fuel-type-modal',
          title: 'Fuel type',
          isModalVisible: false,
          statePropertyName: 'selectedFuelTypes',
        },
        {
          name: 'lifestyle-modal',
          title: 'Lifestyle category',
          isModalVisible: false,
          statePropertyName: 'selectedLifestyles',
        },
        {
          name: 'towing-capacity-modal',
          title: 'Braked towing capacity',
          isModalVisible: false,
          statePropertyName: 'selectedTowingCapacities',
        },
        {
          name: 'emissions-rating-modal',
          title: 'Emissions rating',
          isModalVisible: false,
          statePropertyName: 'selectedEmissionsRatings',
        },
        {
          name: 'transmission-type-modal',
          title: 'Transmission',
          category: 'engine-transmission',
          isModalVisible: false,
          statePropertyName: 'selectedTransmissionTypes',
        },
        {
          name: 'drive-type-modal',
          title: 'Drive type',
          category: 'engine-transmission',
          isModalVisible: false,
          statePropertyName: 'selectedDriveTypes',
        },
        {
          name: 'engine-type-modal',
          title: 'Engine type',
          category: 'engine-transmission',
          isModalVisible: false,
          statePropertyName: 'selectedEngineTypes',
        },
        {
          name: 'cylinder-modal',
          title: 'Cylinders',
          category: 'engine-transmission',
          isModalVisible: false,
          statePropertyName: 'selectedCylinders',
        },
        {
          name: 'engine-capacity-modal',
          title: 'Engine capacity',
          category: 'engine-transmission',
          isModalVisible: false,
          statePropertyName: 'selectedEngineCapacities',
        },
        {
          name: 'fuel-score-modal',
          title: 'Fuel economy',
          category: 'engine-transmission',
          isModalVisible: false,
          statePropertyName: 'selectedFuelScores',
        },
        {
          name: 'driving-range-modal',
          title: 'Driving range',
          category: 'electric',
          isModalVisible: false,
          statePropertyName: 'selectedDrivingRanges',
        },
        {
          name: 'safety-rating-modal',
          title: 'ANCAP safety rating',
          category: 'safety',
          isModalVisible: false,
          statePropertyName: 'selectedSafetyRatings',
        },
        {
          name: 'seat-modal',
          title: 'Seats',
          category: 'style',
          isModalVisible: false,
          statePropertyName: 'selectedSeats',
        },
        {
          name: 'door-modal',
          title: 'Doors',
          category: 'style',
          isModalVisible: false,
          statePropertyName: 'selectedDoors',
        },
        {
          name: 'feature-modal',
          title: 'Features & technology',
          category: 'style',
          isModalVisible: false,
          statePropertyName: 'selectedFeatures',
        },
        {
          name: 'suggestion-modal',
          isModalVisible: false,
          modalOnly: true,
        },
      ],
    };
  },

  mounted() {
    this.showInitialModal(this.modalToDisplayOnLoad);
    // prevent scrolling when modal is open
    document.body.style.overflowY = 'hidden';
  },

  unmounted() {
    document.body.style.overflowY = '';
  },

  computed: {
    ...mapState(useContentStore, ['makeModelModalProps']),
    isMainModalVisible() {
      const childModalVisible = this.modalSections.find((modalSectionItem) => modalSectionItem.isModalVisible);
      return childModalVisible === undefined;
    },
    pillItemsSelected() {
      return this.countItems(this.selectStore.selectedMakes) > 0 || this.countItems(this.selectStore.selectedModels) > 0;
    },
    modalToDisplay() {
      return this.modalSections.find((modalItem) => modalItem.isModalVisible).name;
    },
    resultCount() {
      return `(${this.availableStore.totalCount})`;
    },
  },
  methods: {
    closeClicked() {
      this.$emit('close-modal'); // automatic case translation doesn't seem to work here
    },
    pushToAnalytics() {
      try {
        this.$analytics().push(AnalyticsEvents.SearchModalSearchClick, {
          searchInfo: {
            onsiteSearchType: AnalyticsEventTypes.CarSearch,
          },
        });
      } catch (error) {
        console.warn('fail to push data to analytics', error);
      }
    },
    showSuggestions() {
      const suggestionModal = this.modalSections.find((modalItem) => modalItem.name === 'suggestion-modal');
      if (!suggestionModal) return;
      this.pushToAnalytics();
      this.showModal(suggestionModal);
    },
    hasMakeSelected() {
      return this.selectStore.selectedMakes?.length > 0;
    },
    hasModelSelected() {
      return this.selectStore.selectedModels?.length > 0;
    },
    removeMake(displayText) {
      this.selectStore.removeMakeAndRelatedModels(displayText);
    },
    removeModel(displayText, model) {
      this.selectStore.removeMakeModelAndRelatedBadges(model);
    },
    removeItem(displayText, model, contextObject) {
      if (!contextObject) return;

      const valueToRemove = JSON.stringify(model);

      // find the item to remove
      const indexOfItem = this.selectStore[contextObject.statePropertyName].findIndex((valueItem) => JSON.stringify(valueItem) === valueToRemove);
      this.selectStore[contextObject.statePropertyName].splice(indexOfItem, 1);
    },
    removeRange(displayText, model, contextObject) {
      this.selectStore[contextObject.statePropertyName] = {};
    },
    countItems(items) {
      return items ? items.length : 0;
    },
    clearAll() {
      this.selectStore.$reset();
    },
    closeAll() {
      this.closeClicked();
    },
    getEnrichedModalSections() {
      // Need to do this here. We can't do this directly in the data properties as it's too early in the lifecycle but we need to make it reactive
      this.modalSections.forEach((modalSectionItem) => {
        modalSectionItem.items = this.selectStore[modalSectionItem.statePropertyName];
        if (!modalSectionItem?.removePreviewItemCallback) modalSectionItem.removePreviewItemCallback = this.removeItem;

        switch (modalSectionItem.name) {
          case 'badge-modal':
            modalSectionItem.disabled = !this.hasModelSelected();
            break;
          case 'model-modal':
            modalSectionItem.disabled = !this.hasMakeSelected();
            break;
          default:
            break;
        }
      });

      return this.modalSections;
    },

    getEnrichedMainModalSections() {
      return this.getEnrichedModalSections().filter((modalItem) => !Object.hasOwn(modalItem, 'category') && !modalItem?.modalOnly);
    },

    getEnrichedCategoryModalSections(categoryName) {
      return this.getEnrichedModalSections().filter((modalItem) => modalItem.category === categoryName);
    },

    getVisibilityProperty(nameOfModalSection) {
      const modalSection = this.modalSections.find((modalSectionItem) => modalSectionItem.name === nameOfModalSection);
      return modalSection.isModalVisible;
    },

    showInitialModal(modalName) {
      this.modalSections.forEach((modalSectionItem) => (modalSectionItem.isModalVisible = false));
      if (!modalName) return;

      const modalToShow = this.modalSections.find((modalSectionItem) => modalSectionItem.name === modalName);

      if (!modalToShow.isModalVisible) this.showModal(modalToShow);
    },

    showModal(modalSection) {
      if (modalSection.disabled) {
        return;
      }

      modalSection.isModalVisible = true;
    },

    closeModalByName(nameOfModalSection) {
      const modalToClose = this.modalSections.find((modalSectionItem) => modalSectionItem.name === nameOfModalSection);
      this.closeModal(modalToClose);
    },

    closeModal(modalSection) {
      if (!modalSection) {
        modalSection = this.modalSections.find((modalItem) => modalItem.isModalVisible);
      }
      modalSection.isModalVisible = false;
    },
    clickCategoryModals(categoryName) {
      const categoryToCollapseExpand = this.modalCategories.find((modalCategoryItem) => modalCategoryItem.name === categoryName);
      categoryToCollapseExpand.collapsed = !categoryToCollapseExpand.collapsed;
    },
    performSearch() {
      this.selectStore.toggleResultsUpdated(true); //set the flag to update the results when user click search button
      const url = this.urlBuilder.getSearchResultsUrl();

      if (this.router.currentRoute.value.name !== RoutesEnum.SEARCH_RESULTS) {
        this.urlNavigator.navigateTo(url, { replace: true });
      }

      this.$emit('close-modal');
    },
  },
};
</script>

<style src="@/assets/css/components/make-model-modal.scss" lang="scss"></style>
